import { useEffect, useState } from 'react'
import { Alert, Stack, TextInput } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  collectCredentialIssues,
  licenseRowSchema,
  npiNumberSchema,
  professionalRoleSchema,
  specialtySchema,
  type LicenseRow,
} from '@behuman/core-contract'
import { SaveFooter } from '@/components/ui/SaveFooter'
import { useUpdateMyCredentials, type MyProfile, type SelfLicenseRow } from '@/hooks/useMyProfile'
import { LicensesField } from '@/pages/Super/components/LicensesField'
import { uploadPendingFile } from '@/pages/Super/components/LicensesField.utils'
import { ProfessionalRoleFields } from '@/pages/Super/components/ProfessionalRoleFields'

// A state license as its holder edits it: the self-editable fields of the shared row (the
// contract refuses the License Status, Restriction and verification record from a member),
// plus a picked Supporting Document riding on its row until Save turns it into a Binary.
const selfLicenseFormRowSchema = licenseRowSchema
  .pick({ state: true, licenseNumber: true, effectiveDate: true, expiryDate: true, document: true })
  .extend({ pendingFile: z.instanceof(File).nullish() })

// One form for the tab: the cross-row rules (one license per state, a license needs a
// Professional Role, a Specialty the role carries) are the ones Provider Operations applies.
const professionalSchema = z
  .object({
    professionalRole: professionalRoleSchema.nullable(),
    specialty: specialtySchema.nullable(),
    npiNumber: npiNumberSchema,
    licenses: z.array(selfLicenseFormRowSchema),
  })
  .superRefine((data, ctx) => {
    for (const issue of collectCredentialIssues(data)) {
      ctx.addIssue({ code: 'custom', ...issue })
    }
  })
type ProfessionalFormValues = z.infer<typeof professionalSchema>
type SelfLicenseFormRow = ProfessionalFormValues['licenses'][number]

// The stored license as the member edits and re-sends it: the self-editable fields only.
// An unchanged row is matched by state and kept in full by the middleware; a changed one
// comes back Unverified (the Unverified rule).
const toSelfLicenseRow = (license: LicenseRow): SelfLicenseRow => ({
  state: license.state,
  licenseNumber: license.licenseNumber,
  effectiveDate: license.effectiveDate,
  expiryDate: license.expiryDate,
  document: license.document,
})

const toValues = (profile: MyProfile): ProfessionalFormValues => ({
  professionalRole: profile.professionalRole,
  specialty: profile.specialty,
  npiNumber: profile.npiNumber ?? '',
  licenses: profile.licenses.map(toSelfLicenseRow),
})

// The step a failed Save names in its alert: the document upload runs first, then the write.
const UPLOAD_FAILED = 'Document not uploaded'
const WRITE_FAILED = 'Credentials not saved'

/**
 * Professional details: the member's own Credentials — NPI, Professional Role, Specialty
 * and state licenses (the `self` card: state, number, dates and the Supporting Document) —
 * with one Save for the tab. Save creates a Binary for each picked document with the
 * viewer's own client before the write, as Provider Operations does. A failed upload or
 * write keeps the edits and the picked files, so the retry needs no second pick; a Binary a
 * failed write left behind is never reused. Shown only to a member with an active Medical
 * doctor role.
 */
export function ProfessionalDetailsTab({
  profile,
  onDirtyChange,
}: {
  profile: MyProfile
  /** Tells the page whether the tab holds unsaved edits (the tab dot, the reload warning). */
  onDirtyChange?: (dirty: boolean) => void
}) {
  const medplum = useMedplum()
  const form = useForm<ProfessionalFormValues>({
    resolver: zodResolver(professionalSchema),
    defaultValues: toValues(profile),
  })
  const update = useUpdateMyCredentials()
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const { isDirty } = form.formState
  const isSaving = isUploading || update.isPending

  // Re-populated after a refetch only while the form holds no edits: react-query refetches
  // in the background, and a reset then would drop what the member is typing.
  useEffect(() => {
    if (!isDirty) form.reset(toValues(profile))
  }, [profile, isDirty, form])
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const uploadPendingDocuments = async (
    rows: readonly SelfLicenseFormRow[],
  ): Promise<SelfLicenseRow[]> =>
    Promise.all(
      rows.map(async ({ pendingFile, ...row }) =>
        pendingFile ? { ...row, document: await uploadPendingFile(medplum, pendingFile) } : row,
      ),
    )

  const save = async (values: ProfessionalFormValues) => {
    setUploadError(null)
    update.reset()
    let licenses: SelfLicenseRow[]
    setIsUploading(true)
    try {
      licenses = await uploadPendingDocuments(values.licenses)
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Unknown error')
      return
    } finally {
      setIsUploading(false)
    }
    try {
      await update.mutateAsync({
        npiNumber: values.npiNumber,
        professionalRole: values.professionalRole,
        specialty: values.specialty,
        licenses,
      })
      // The refetch has landed (the hook awaits it); what was sent is what is now stored,
      // the uploaded documents included.
      form.reset({ ...values, licenses })
    } catch {
      // The hook raised the toast and the footer shows it; the edits stay for a retry.
    }
  }

  const cancel = () => {
    form.reset()
    setUploadError(null)
    update.reset()
  }

  const saveError = uploadError
    ? { title: UPLOAD_FAILED, message: uploadError }
    : update.error
      ? { title: WRITE_FAILED, message: update.error.message }
      : null

  return (
    <form onSubmit={form.handleSubmit(save)} noValidate>
      <fieldset disabled={isSaving} className="m-0 min-w-0 border-0 p-0">
        <Stack gap="sm">
          <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
            Changes to credentials may require administrator verification
          </Alert>
          <Controller
            name="npiNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                label="NPI Number"
                placeholder="Enter NPI number"
                error={fieldState.error?.message}
              />
            )}
          />
          <ProfessionalRoleFields control={form.control} setValue={form.setValue} />
          <LicensesField control={form.control} setValue={form.setValue} variant="self" />
        </Stack>
      </fieldset>
      <SaveFooter saveError={saveError} isSaving={isSaving} isDirty={isDirty} onCancel={cancel} />
    </form>
  )
}
